import { MantineProvider } from '@mantine/core'
import { DatesProvider } from '@mantine/dates'
import { ModalsProvider } from '@mantine/modals'
import { Notifications } from '@mantine/notifications'
import * as Sentry from '@sentry/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import React from 'react'
import ReactDOM from 'react-dom/client'
import { registerServiceWorker } from './lib/pushNotifications'
import { theme } from './lib/theme'
import { trpc, trpcClient } from './lib/trpc'
import { routeTree } from './routeTree.gen'
import { captureAcquisitionData, storeAcquisitionData } from './utils/utm'

// Import Mantine styles with proper layering
import '@mantine/carousel/styles.css'
import '@mantine/core/styles.layer.css'
import '@mantine/dates/styles.layer.css'
import '@mantine/notifications/styles.layer.css'
import './styles/global.css'

// Configure dayjs for Finnish locale
import 'dayjs/locale/fi'

// Capture acquisition data immediately on page load
storeAcquisitionData(captureAcquisitionData())

// Create a new router instance
const router = createRouter({
  routeTree,
  defaultPreload: false,
  scrollRestoration: true,
  defaultErrorComponent: ({ error }) => {
    Sentry.captureException(error)
    return <ErrorFallback error={error} />
  },
})

// Initialize Sentry
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [Sentry.tanstackRouterBrowserTracingIntegration(router)],
    tracesSampleRate: 1.0,
  })
}

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors, but retry on network errors
        if (error?.data?.httpStatus >= 400 && error?.data?.httpStatus < 500) {
          return false
        }
        return failureCount < 3
      },
    },
  },
})

function ErrorFallback({ error }: { error: Error }) {
  const errorMessage =
    error instanceof Error ? error.message : 'Tuntematon virhe'
  return (
    <div
      style={{
        padding: '2rem',
        textAlign: 'center',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      <h1>Jotain meni vikaan</h1>
      <p>Päivitä sivu tai yritä myöhemmin uudelleen.</p>
      {import.meta.env.DEV && (
        <details style={{ marginTop: '1rem', textAlign: 'left' }}>
          <summary>Virheen tiedot (kehitystila)</summary>
          <pre
            style={{
              padding: '1rem',
              background: '#f5f5f5',
              borderRadius: '4px',
              overflow: 'auto',
            }}
          >
            {errorMessage}
          </pre>
        </details>
      )}
      <button
        onClick={() => window.location.reload()}
        style={{
          marginTop: '1rem',
          padding: '0.5rem 1rem',
          background: '#228be6',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
        }}
      >
        Päivitä sivu
      </button>
    </div>
  )
}

function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error }) => (
        <ErrorFallback
          error={error instanceof Error ? error : new Error(String(error))}
        />
      )}
    >
      <MantineProvider theme={theme} forceColorScheme="light">
        <ModalsProvider>
          <DatesProvider
            settings={{
              locale: 'fi',
              firstDayOfWeek: 1, // Monday
              weekendDays: [0, 6], // Sunday and Saturday
              consistentWeeks: true,
            }}
          >
            <Notifications position="top-right" />
            <RouterProvider router={router} />
          </DatesProvider>
        </ModalsProvider>
      </MantineProvider>
    </Sentry.ErrorBoundary>
  )
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </trpc.Provider>
  </React.StrictMode>,
)

// Register service worker for push notifications
if ('serviceWorker' in navigator) {
  registerServiceWorker()
}
